@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.menu.clients

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import us.laantech.common.ui.DataTableView
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.admin.ui.CreateEditClientView
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.api.KtorClient
import us.laantech.routes.users.UserDTO

data class ClientListUiState(
    val users: List<UserDTO> = listOf<UserDTO>(),
    val clients: List<ParentCompany> = emptyList(),
    // Branches are a separate collection now; store counts keyed by accountSub.
    val branchCounts: Map<String, Int> = emptyMap(),

    val showEditClientModal: Boolean = false,
    val selectedClient: ParentCompany? = null,
)

@Composable
fun ClientListView(
    onNavigateToOnboarding: () -> Unit = {},
    viewModel: ClientListViewModel = viewModel(
        factory = viewModelFactory {
            initializer { ClientListViewModel(onNavigateToOnboarding) }
        }
    )
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.loadAccounts()
        viewModel.loadClients()
    }

    Column {
        Text("Internal Clients")

        DataTableView<ParentCompany>(
            modifier = Modifier.fillMaxSize(),
            rows = uiState.clients,
        ) {
            column("Owner Account", weight = 1f) {
                val username = uiState.users.firstOrNull { user -> user.sub == it.accountSub }?.username ?: ""
                Text(username, modifier = Modifier.weight(1f))
            }

            column("Company Name", weight = 1f) { Text("${it.companyInfo?.companyName}", modifier = Modifier.weight(1f)) }
            column("Phone", weight = 1f) { Text("${it.contactInfo?.phoneNumberPrimary}", modifier = Modifier.weight(1f)) }
            column("Stores", weight = 1f) { Text("${uiState.branchCounts[it.uuid] ?: 0}", modifier = Modifier.weight(1f)) }
            column("Card Rate", weight = 1f) {
                val rate5dp = it.cardTransactionRate5dp ?: 0L
                val txRateStr = "${rate5dp.toBigDecimal().div(1000.toBigDecimal()).toPlainString()}%"
                Text(txRateStr, modifier = Modifier.weight(1f))
            }
            column("Actions", weight = 1f) {
                Row(Modifier.weight(1f)) {
                    Button({ viewModel.toggleEditClientModal(it) }) {
                        Text("Edit")
                    }
                }
            }
        }
    }

    if (uiState.showEditClientModal) {
        EditClientModal(viewModel)
    }

}

@Composable
fun EditClientModal(viewModel: ClientListViewModel) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    BasicAlertDialog({ viewModel.toggleEditClientModal(null) }) {
        Surface {
            Column(Modifier.verticalScroll(rememberScrollState())) {
                Text("Edit Client")
                CreateEditClientView(
                    initialClient = uiState.selectedClient,
                    onSubmitSuccess = {
                        viewModel.toggleEditClientModal(null)
                        viewModel.loadClients()
                    },
                )
            }
        }
    }
}





class ClientListViewModel(
    private val onNavigateToOnboarding: () -> Unit = {},
): ViewModel() {

    private val _uiState = MutableStateFlow(ClientListUiState())
    val uiState = _uiState.asStateFlow()
    val client = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)

    fun loadAccounts() {
        scope.launch {
            val response = client.get("/admin/accounts/list")
            println("Response: $response")

            val users = response.body<List<UserDTO>>()

            println("Users: $users")

            _uiState.value = _uiState.value.copy(users = users)
        }
    }

    fun loadClients() {
        scope.launch {
            val response = client.get("/admin/internal-clients/list") {

            }

            val clients = response.body<List<ParentCompany>>()

            println("Clients: $clients")

            _uiState.value = _uiState.value.copy(clients = clients)

            // Branches live in a separate collection; fetch counts per company (keyed by uuid).
            val counts = clients.mapNotNull { c -> c.uuid?.let { it to fetchBranches(c).size } }.toMap()
            _uiState.update { it.copy(branchCounts = counts) }
        }
    }

    private suspend fun fetchBranches(company: ParentCompany): List<CompanyBranch> {
        val response = client.get("/admin/internal-clients/list/branches") {
            parameter("accountSub", company.accountSub)
            company.uuid?.let { parameter("parentCompanyUuid", it) }
        }
        return response.body()
    }



    fun openClientOnboardingPage() {
        onNavigateToOnboarding()
    }

    fun toggleEditClientModal(client: ParentCompany?) {
        _uiState.update { it.copy(showEditClientModal = !it.showEditClientModal, selectedClient = client) }
    }

}